/**
 * VendorFinderDialog — discovery flow modal opened from per-trade CTAs
 * on VendorDirectoryPanel.
 *
 * Layout (landscape, 13" laptop friendly):
 *   ┌──────────────────────────────────────────────────────┐
 *   │ Header                                                │
 *   ├──────────────────────────────────────────────────────┤
 *   │ Filters (segmented) · ⚡ Curate 2-3                 │
 *   ├──────────────────────────────────────────────────────┤
 *   │ Search a specific business (Google, session-billed)  │
 *   ├──────────────────────┬───────────────────────────────┤
 *   │ Candidates (scrolls) │ Map: home pin + numbered      │
 *   │ Each row labelled    │ vendor dots, auto-fit viewport│
 *   │ 1, 2, 3 …            │                               │
 *   ├──────────────────────┴───────────────────────────────┤
 *   │ Manual form panel (toggles in/out)                    │
 *   ├──────────────────────────────────────────────────────┤
 *   │ + Add manually        Cancel · Save N selected        │
 *   └──────────────────────────────────────────────────────┘
 *
 * Commit paths:
 *   1. Multi-select Google candidates → Save N selected
 *   2. ⚡ Curate 2-3 by rating × reviews → one click
 *   3. Search row → pick a Google listing → Add to directory
 *   4. + Add manually (footer left) → form panel → Add to directory
 *
 * Map: Google Maps Static API. Blue "H" marker for the home address,
 * red marker for the focused candidate. Auto-fits viewport to both pins.
 * Falls back to a placeholder when VITE_GOOGLE_MAPS_API_KEY is missing.
 */
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  CircularProgress,
  Dialog as MuiDialog,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { AdvancedMarker, Map, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import { CloseButton } from '@/components/ds';
import { useVendorCandidates, useVendors } from '@/hooks/useVendors';
import {
  VENDOR_TRADE_LABELS,
  type CreateVendorInput,
  type Vendor,
  type VendorCandidate,
  type VendorTrade,
} from '@/models/Vendor';
import { AUTO_PICK_CRITERIA } from '@/services/api/vendors';
import {
  fetchBusinessSuggestions,
  placeToBusinessMatch,
  type PlacesBusinessMatch,
} from '@/types/maps';

const C = {
  bg2: 'var(--tx-glass-panel)',
  bg3: 'var(--tx-glass-panel)',
  bg4: '#383838',
  lime: '#c0ff02',
  text: 'var(--tx-text)',
  muted: 'var(--tx-faint)',
  subtle: 'var(--tx-muted)',
  border: 'var(--tx-line)',
};

const fontStack = "'Manrope',sans-serif";

const GoogleG = (
  <svg width="12" height="12" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FBBC04"
      d="M9.83 24c0-1.52.25-2.98.7-4.36L2.62 13.6C1.08 16.73.2 20.26.2 24s.88 7.27 2.42 10.4l7.91-6.05c-.45-1.38-.7-2.83-.7-4.35z"
    />
    <path
      fill="#EA4335"
      d="M23.7 10.13c3.31 0 6.3 1.17 8.65 3.09L39.21 6.45C35.05 2.85 29.7.59 23.7.59c-9.36 0-17.4 5.35-21.08 13.01l7.91 6.04C12.33 14.04 17.55 10.13 23.7 10.13z"
    />
    <path
      fill="#34A853"
      d={[
        'M23.7 37.87c-6.15 0-11.37-3.91-13.17-9.52',
        'l-7.91 6.04c3.68 7.66 11.72 13.01 21.08 13.01',
        '5.74 0 11.22-2.04 15.34-5.86l-7.5-5.81',
        'c-2.13 1.34-4.83 2.14-7.84 2.14z',
      ].join(' ')}
    />
    <path
      fill="#4285F4"
      d="M46.98 24c0-1.36-.21-2.81-.53-4.17H23.7v8.85h13.09c-.65 3.22-2.45 5.69-5.04 7.39l7.5 5.81C43.6 37.7 46.98 31.45 46.98 24z"
    />
  </svg>
);


/**
 * Criteria tooltip — matches the project's HoverTip style spec from
 * HomeownerHub: translucent dark fill with backdrop blur, no border,
 * no shadow, Manrope 11px 400. Pops *below* the button (the Auto-pick
 * sits near the top of the dialog and the dialog uses overflow:hidden
 * to clip its rounded corners, so a tooltip popping up would be
 * clipped).
 */
function CriteriaTooltip({ children }: { children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div
          role="tooltip"
          data-testid="auto-pick-tooltip"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            background: 'var(--tx-glass)',
            boxShadow: 'var(--tx-shadow-sm)',
            backdropFilter: 'var(--tx-blur)',
            WebkitBackdropFilter: 'var(--tx-blur)',
            borderRadius: '9px',
            padding: '8px 12px',
            fontSize: 11,
            color: 'var(--tx-text)',
            fontFamily: fontStack,
            fontWeight: 400,
            lineHeight: 1.5,
            pointerEvents: 'none',
            zIndex: 9999,
            width: 240,
            textAlign: 'left',
          }}>
          <div style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>
            Curates 2-3 vendors when available:
          </div>
          <div>
            ★ {AUTO_PICK_CRITERIA.minRating.toFixed(1)}+ rating
            <span style={{ opacity: 0.5, margin: '0 6px' }}>·</span>
            {AUTO_PICK_CRITERIA.minReviewCount}+ reviews
          </div>
          <div
            style={{
              marginTop: 4,
              color: 'var(--tx-faint)',
              fontStyle: 'italic',
            }}>
            Ranked by rating × review confidence.
          </div>
        </div>
      )}
    </div>
  );
}

type Filters = {
  minRating?: number;
  maxDistanceMi?: number;
  minReviewCount?: number;
  maxPriceLevel?: number;
};





const DEFAULT_TEXT_QUERY_BY_TRADE: Partial<Record<VendorTrade, string>> = {
  other_specialty: 'home repair contractor',
};









function ManualForm({
  trade,
  onAdd,
  onCancel,
  isSubmitting = false,
  isMobile = false,
}: {
  trade: VendorTrade;
  onAdd: (input: Omit<CreateVendorInput, 'propertyId'>) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  isMobile?: boolean;
}) {
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');

  const canSubmit = companyName.trim().length > 0 && !isSubmitting;

  const submit = () => {
    if (!canSubmit) return;
    onAdd({
      trade,
      source: 'manual',
      name: companyName.trim(),
      contactName: contactName.trim() || undefined,
      address: address.trim() || undefined,
      phone: phone.trim() || undefined,
      email: email.trim() || undefined,
      notes: notes.trim() || undefined,
    });
  };

  // the app's field: a label above, a 40px tonal box, no outline
  const field = (
    label: string,
    value: string,
    set: (v: string) => void,
    opts: { placeholder?: string; type?: string; testId?: string; required?: boolean; wide?: boolean } = {},
  ) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: opts.wide ? '1 / -1' : undefined }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: C.muted }}>
        {label}
        {opts.required && <span style={{ color: C.text }}> *</span>}
      </span>
      <input
        data-testid={opts.testId}
        type={opts.type ?? 'text'}
        value={value}
        placeholder={opts.placeholder}
        onChange={(e) => set(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && submit()}
        style={{
          height: 40,
          padding: '0 14px',
          border: 0,
          borderRadius: 12,
          outline: 'none',
          background: 'var(--tx-fill)',
          color: C.text,
          fontFamily: fontStack,
          fontSize: 13,
          boxSizing: 'border-box',
          width: '100%',
        }}
      />
    </label>
  );

  return (
    <div data-testid="finder-manual-form" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '18px 14px 12px' : '28px 32px 20px' }}>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, maxWidth: 520, lineHeight: 1.5 }}>
          A pro a neighbour recommended, or one you&apos;ve used before. Only the company name is needed; add the rest if
          you have it.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px 20px', maxWidth: 720 }}>
          {field('Company', companyName, setCompanyName, { required: true, testId: 'finder-manual-company', placeholder: 'e.g. Bright Spark Electric' })}
          {field('Contact', contactName, setContactName, { placeholder: 'Who you talk to' })}
          {field('Phone', phone, setPhone, { type: 'tel' })}
          {field('Email', email, setEmail, { type: 'email' })}
          {field('Address', address, setAddress, { wide: true })}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: '1 / -1' }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: C.muted }}>Notes</span>
            <textarea
              value={notes}
              placeholder="How you found them, what they did, anything worth remembering"
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              style={{
                padding: '12px 14px',
                border: 0,
                borderRadius: 12,
                outline: 'none',
                resize: 'vertical',
                background: 'var(--tx-fill)',
                color: C.text,
                fontFamily: fontStack,
                fontSize: 13,
                lineHeight: 1.5,
              }}
            />
          </label>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: isMobile ? '14px 14px 16px' : '18px 32px 28px',
          borderTop: '1px solid var(--tx-line)',
          flexShrink: 0,
        }}>
        <button
          type="button"
          onClick={onCancel}
          className="hx-fd-quiet"
          style={{ border: 0, background: 'transparent', padding: 0, fontFamily: fontStack, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
          Back to results
        </button>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          data-testid="finder-manual-submit"
          onClick={submit}
          disabled={!canSubmit}
          style={{
            height: 40,
            padding: '0 24px',
            border: 0,
            borderRadius: 10,
            background: canSubmit ? 'var(--tx-lime)' : 'var(--tx-fill)',
            color: canSubmit ? '#262626' : C.subtle,
            fontFamily: fontStack,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            cursor: canSubmit ? 'pointer' : 'default',
          }}>
          Add to directory
        </button>
      </div>
    </div>
  );
}

/**
 * Search Google for a specific business by name and add it directly —
 * separate from ManualForm's typed-by-hand flow. Uses the new session-token
 * based Autocomplete API: keystrokes within a session are free, and the one
 * billed call happens when the user picks a result (see types/maps.ts).
 */
function BusinessSearchRow({
  trade,
  onAdd,
  isSubmitting,
}: {
  trade: VendorTrade;
  onAdd: (input: Omit<CreateVendorInput, 'propertyId'>) => Promise<void>;
  isSubmitting: boolean;
}) {
  const placesLib = useMapsLibrary('places');
  const [sessionToken, setSessionToken] =
    useState<google.maps.places.AutocompleteSessionToken | null>(null);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [matchedPlace, setMatchedPlace] = useState<PlacesBusinessMatch | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (placesLib && !sessionToken) {
      setSessionToken(new placesLib.AutocompleteSessionToken());
    }
  }, [placesLib, sessionToken]);

  useEffect(() => () => clearTimeout(searchDebounceRef.current), []);

  const searchBusinesses = (value: string) => {
    clearTimeout(searchDebounceRef.current);
    if (!placesLib || !sessionToken || value.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        setSuggestions(await fetchBusinessSuggestions(value.trim(), placesLib, sessionToken));
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  const pickSuggestion = async (suggestion: google.maps.places.AutocompleteSuggestion) => {
    setIsResolving(true);
    try {
      const match = await placeToBusinessMatch(suggestion);
      if (!match) return;
      setMatchedPlace(match);
      setQuery(match.name);
      setSuggestions([]);
      // The session that just resolved is spent — start a fresh one so a
      // follow-up search (after cancel) isn't billed per-request.
      if (placesLib) setSessionToken(new placesLib.AutocompleteSessionToken());
    } finally {
      setIsResolving(false);
    }
  };

  const reset = () => {
    setQuery('');
    setMatchedPlace(null);
    setSuggestions([]);
  };

  const confirmAdd = async () => {
    if (!matchedPlace) return;
    await onAdd({
      trade,
      source: 'google_places',
      name: matchedPlace.name,
      place_id: matchedPlace.place_id,
      address: matchedPlace.address,
      phone: matchedPlace.phone,
      internationalPhoneNumber: matchedPlace.internationalPhoneNumber,
      website: matchedPlace.website,
      googleMapsUri: matchedPlace.googleMapsUri,
      rating: matchedPlace.rating,
      reviewCount: matchedPlace.reviewCount,
      priceLevel: matchedPlace.priceLevel,
      location: matchedPlace.location,
      photoUrl: matchedPlace.photoUrl,
      category: matchedPlace.category,
    });
    reset();
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        flexShrink: 0,
      }}>
      <Autocomplete
        freeSolo
        fullWidth
        options={suggestions}
        filterOptions={(x) => x}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.placePrediction?.text.text ?? ''
        }
        inputValue={query}
        onInputChange={(_, newValue, reason) => {
          if (reason === 'reset') return;
          setQuery(newValue);
          if (matchedPlace && newValue !== matchedPlace.name) setMatchedPlace(null);
          searchBusinesses(newValue);
        }}
        onChange={(_, newValue) => {
          if (newValue && typeof newValue !== 'string') {
            pickSuggestion(newValue).catch((err) => console.error('pickSuggestion failed', err));
          }
        }}
        loading={isSearching || isResolving}
        data-testid="finder-business-search"
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search Google for a specific business..."
            variant="outlined"
            size="small"
            slotProps={{
              htmlInput: { ...params.inputProps, 'data-testid': 'finder-business-search-input' },
              input: {
                ...params.InputProps,
                startAdornment: <SearchIcon sx={{ fontSize: 16, color: C.muted, mr: 0.5 }} />,
                endAdornment: (
                  <>
                    {(isSearching || isResolving) && (
                      <CircularProgress size={14} sx={{ color: C.subtle, mr: 1 }} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
            sx={{
              // the app's field: the filters' height, a tight radius, a tonal fill, no outline
              '& .MuiOutlinedInput-root': {
                fontFamily: fontStack,
                fontSize: 13,
                color: C.text,
                minHeight: 38,
                borderRadius: '10px',
                bgcolor: 'var(--tx-fill)',
                '& fieldset': { border: 0 },
              },
              '& .MuiInputBase-input::placeholder': { color: C.muted, opacity: 1, fontSize: 13 },
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.placePrediction?.placeId}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
              <span style={{ flexShrink: 0, display: 'flex' }}>{GoogleG}</span>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    color: C.text,
                    fontFamily: fontStack,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  {option.placePrediction?.mainText?.text ?? option.placePrediction?.text.text}
                </div>
                {option.placePrediction?.secondaryText && (
                  <div
                    style={{
                      fontSize: 11,
                      color: C.muted,
                      fontFamily: fontStack,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                    {option.placePrediction.secondaryText.text}
                  </div>
                )}
              </div>
            </div>
          </li>
        )}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: C.bg3,
              border: 0,
              borderRadius: '9px',
              mt: '4px',
            },
          },
        }}
      />
      {matchedPlace && query === matchedPlace.name && (
        <div
          data-testid="finder-business-search-match"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'var(--tx-fill)',
            border: 0,
            borderRadius: 8,
            padding: '10px 12px',
          }}>
          <span style={{ flexShrink: 0, display: 'flex' }}>{GoogleG}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: C.text,
                fontFamily: fontStack,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {matchedPlace.name}
            </div>
            <div
              style={{
                fontSize: 11,
                color: C.muted,
                fontFamily: fontStack,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {[
                matchedPlace.address,
                matchedPlace.rating != null
                  ? `★ ${matchedPlace.rating.toFixed(1)} (${matchedPlace.reviewCount ?? 0})`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          <button
            onClick={reset}
            style={{
              background: 'var(--tx-fill)',
              border: 0,
              color: C.subtle,
              borderRadius: 8,
              padding: '6px 12px',
              cursor: 'pointer',
              fontFamily: fontStack,
              fontSize: 12,
              flexShrink: 0,
            }}>
            Cancel
          </button>
          <button
            data-testid="finder-business-search-add"
            disabled={isSubmitting}
            onClick={() => confirmAdd().catch((err) => console.error('confirmAdd failed', err))}
            style={{
              background: C.lime,
              border: 'none',
              color: '#111',
              borderRadius: 8,
              padding: '6px 12px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.6 : 1,
              fontFamily: fontStack,
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
            }}>
            Add to directory
          </button>
        </div>
      )}
    </div>
  );
}

const FINDER_CSS = `
.hx-fd-row { transition: background 0.12s ease; }
.hx-fd-row:hover, .hx-fd-row[data-active='true'] { background: var(--tx-fill); }
.hx-fd-link { display: inline-flex; align-items: center; height: 30px; padding: 0 12px; border-radius: 8px;
  background: var(--tx-fill); color: var(--tx-text); font-size: 12px; font-weight: 500; text-decoration: none; }
.hx-fd-link:hover { background: var(--tx-fill-2); }
.hx-fd-primary { transition: background 0.12s ease; }
.hx-fd-primary:hover:not(:disabled) { background: var(--tx-lime-hover) !important; }
.hx-fd-quiet { color: var(--tx-muted); transition: color 0.12s ease; }
.hx-fd-quiet:hover { color: var(--tx-text); }
`;

// every result on one toned map, numbered like the list; the one you're on is ink, your shortlist lime
function ResultsMap({
  candidates,
  activeId,
  pickedIds,
  onPick,
}: {
  candidates: VendorCandidate[];
  activeId: string | null;
  pickedIds: Set<string>;
  onPick: (id: string) => void;
}) {
  const located = candidates.filter((c) => c.location);
  if (!import.meta.env.VITE_GOOGLE_MAPS_API_KEY || located.length === 0) {
    return <div style={{ height: '100%', display: 'grid', placeItems: 'center', fontSize: 12, color: C.muted }}>Map unavailable</div>;
  }
  const center = {
    lat: located.reduce((n, c) => n + (c.location as { lat: number }).lat, 0) / located.length,
    lng: located.reduce((n, c) => n + (c.location as { lng: number }).lng, 0) / located.length,
  };
  return (
    <div className="hx-map-tone" style={{ width: '100%', height: '100%' }}>
      <Map
        data-testid="finder-service-area-map"
        defaultCenter={center}
        defaultZoom={11}
        gestureHandling="cooperative"
        disableDefaultUI
        style={{ width: '100%', height: '100%' }}
        mapId="vendor-finder-map">
        {candidates.map((c, i) => {
          if (!c.location) return null;
          const on = c.place_id === activeId;
          const picked = pickedIds.has(c.place_id);
          return (
            <AdvancedMarker key={c.place_id} position={{ lat: c.location.lat, lng: c.location.lng }} zIndex={on ? 10 : picked ? 5 : 1}>
              <button
                type="button"
                onClick={() => onPick(c.place_id)}
                aria-label={c.name}
                style={{
                  // a true circle at any number of digits: fixed square box, no button padding
                  width: on ? 26 : 22,
                  height: on ? 26 : 22,
                  minWidth: on ? 26 : 22,
                  padding: 0,
                  boxSizing: 'border-box',
                  aspectRatio: '1 / 1',
                  borderRadius: '50%',
                  border: 0,
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '-0.03em',
                  lineHeight: 1,
                  display: 'grid',
                  placeItems: 'center',
                  background: picked ? '#C0FF02' : on ? '#f7f7f6' : '#3a3a39',
                  color: picked || on ? '#262626' : '#f7f7f6',
                  fontSize: on ? 11 : 9,
                  fontWeight: 700,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.45)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}>
                {i + 1}
              </button>
            </AdvancedMarker>
          );
        })}
      </Map>
    </div>
  );
}

export function VendorFinderDialog({
  trade,
  propertyId,
  savedVendors,
  smartDiscoveryLocked = false,
  onClose,
}: {
  trade: VendorTrade;
  propertyId: string;
  savedVendors: Vendor[];
  smartDiscoveryLocked?: boolean;
  onClose: () => void;
}) {
  const [filters, setFilters] = useState<Filters>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showManual, setShowManual] = useState(false);
  const [previewPlaceId, setPreviewPlaceId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const { createVendor, isCreatingVendor } = useVendors(propertyId);

  const {
    data: candidates = [],
    isLoading,
    isError,
    error: vendorSearchError,
    refetch: retryVendorSearch,
  } = useVendorCandidates(
    smartDiscoveryLocked
      ? null
      : {
          propertyId,
          trade,
          textQuery: DEFAULT_TEXT_QUERY_BY_TRADE[trade],
          filters,
        },
  );

  const savedPlaceTradeKeys = useMemo(
    () =>
      new Set(
        savedVendors.filter((v) => v.place_id).map((v) => `${v.place_id as string}:${v.trade}`),
      ),
    [savedVendors],
  );
  const isSavedForTrade = (candidate: VendorCandidate) =>
    Boolean(
      candidate.place_id && savedPlaceTradeKeys.has(`${candidate.place_id}:${candidate.trade}`),
    );

  const toggle = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Priority: last hovered/selected → first selected (list order) → first candidate
  const effectivePreviewId = (() => {
    if (previewPlaceId && candidates.some((c) => c.place_id === previewPlaceId))
      return previewPlaceId;
    const firstSelected = candidates.find((c) => selectedIds.has(c.place_id));
    if (firstSelected) return firstSelected.place_id;
    return candidates[0]?.place_id ?? null;
  })();

  const previewCandidate = candidates.find((c) => c.place_id === effectivePreviewId) ?? null;

  const saveSelected = async () => {
    const toAdd = candidates.filter((c) => selectedIds.has(c.place_id) && !isSavedForTrade(c));
    if (toAdd.length === 0) return;
    setActionMessage(null);
    try {
      await Promise.all(
        toAdd.map((c) =>
          createVendor({
            propertyId,
            trade: c.trade,
            source: 'google_places',
            name: c.name,
            place_id: c.place_id,
            phone: c.phone,
            internationalPhoneNumber: c.internationalPhoneNumber,
            email: c.email,
            website: c.website,
            googleMapsUri: c.googleMapsUri,
            address: c.address,
            rating: c.rating,
            reviewCount: c.reviewCount,
            priceLevel: c.priceLevel,
            distance: c.distance,
            location: c.location,
            photoUrl: c.photoUrl,
            category: c.category,
            hoursLabel: c.hoursLabel,
          }),
        ),
      );
      onClose();
    } catch (error) {
      console.error('Failed to save vendors:', error);
      setActionMessage('Could not save vendors. Try again.');
    }
  };

  const doAutoPopulate = async () => {
    setActionMessage(null);
    try {
      const available = candidates.filter((candidate) => !isSavedForTrade(candidate));
      const ideal = available.filter(
        (candidate) =>
          (candidate.rating ?? 0) >= AUTO_PICK_CRITERIA.minRating &&
          (candidate.reviewCount ?? 0) >= AUTO_PICK_CRITERIA.minReviewCount,
      );
      const pool = ideal.length > 0 ? ideal : available;
      const toAdd = pool
        .slice()
        .sort(
          (a, b) => (b.rating ?? 0) * (b.reviewCount ?? 0) - (a.rating ?? 0) * (a.reviewCount ?? 0),
        )
        .slice(0, 3);

      if (toAdd.length === 0) {
        setActionMessage('No eligible vendors returned for this trade yet.');
        return;
      }
      await Promise.all(
        toAdd.map((c) =>
          createVendor({
            propertyId,
            trade: c.trade,
            source: 'google_places',
            name: c.name,
            place_id: c.place_id,
            phone: c.phone,
            internationalPhoneNumber: c.internationalPhoneNumber,
            email: c.email,
            website: c.website,
            googleMapsUri: c.googleMapsUri,
            address: c.address,
            rating: c.rating,
            reviewCount: c.reviewCount,
            priceLevel: c.priceLevel,
            distance: c.distance,
            location: c.location,
            photoUrl: c.photoUrl,
            category: c.category,
            hoursLabel: c.hoursLabel,
          }),
        ),
      );
      onClose();
    } catch (error) {
      console.error('Failed to curate vendors:', error);
      setActionMessage('Could not curate vendors. Try again.');
    }
  };

  const addManual = async (input: Omit<CreateVendorInput, 'propertyId'>) => {
    setActionMessage(null);
    try {
      await createVendor({ ...input, propertyId });
      onClose();
    } catch (error) {
      console.error('Failed to add vendor:', error);
      setActionMessage('Could not add vendor. Try again.');
    }
  };

  const selectedCount = selectedIds.size;

  // Center the Paper over the main content area and keep it locked there
  // across all browser resizes for the dialog's lifetime.
  const isMobile = useMediaQuery('(max-width:767px)');
  const [shiftX, setShiftX] = useState(0);
  useEffect(() => {
    const measure = () => {
      const main = document.getElementById('dashboard-main-content');
      if (main) {
        const r = main.getBoundingClientRect();
        setShiftX(r.left + r.width / 2 - window.innerWidth / 2);
      }
    };
    measure();
    const main = document.getElementById('dashboard-main-content');
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <MuiDialog
      data-testid="vendor-finder-dialog"
      open
      onClose={onClose}
      maxWidth={false}
      sx={
        isMobile
          ? {
              '& .MuiDialog-container': {
                alignItems: 'flex-start',
                paddingTop: '81px',
              },
            }
          : undefined
      }
      slotProps={{
        paper: {
          sx: {
            background: C.bg2,
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            border: 0,
            borderRadius: '24px',
            width: '100%',
            maxWidth: isMobile ? '100%' : 1080,
            maxHeight: isMobile ? 'calc(100vh - 180px)' : 'min(90vh, 780px)',
            margin: isMobile ? '0 10px' : undefined,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
            fontFamily: fontStack,
            transform: isMobile ? 'none' : `translateX(${shiftX}px)`,
          },
        },
      }}>
      {/* the app's close: the ds X, top-right, as every other dialog */}
      <span data-testid="finder-close" style={{ position: 'absolute', top: 18, right: 18, zIndex: 2 }}>
        <CloseButton onClose={onClose} label="Close" />
      </span>
      <style>{FINDER_CSS}</style>
      {/* Header: what you're doing, and the one-click way to do it */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, padding: isMobile ? '22px 56px 0 18px' : '32px 80px 0 32px', flexShrink: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 300, letterSpacing: '-0.025em', color: C.text }}>
            {showManual ? 'Add someone you know' : `Find ${VENDOR_TRADE_LABELS[trade].toLowerCase()} pros`}
          </div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
            {showManual
              ? `${VENDOR_TRADE_LABELS[trade]} · saved to your directory`
              : smartDiscoveryLocked
              ? 'Recommendations unlock once your inspection report is uploaded.'
              : isLoading
                ? 'Looking nearby…'
                : `${candidates.length} nearby. Tap + on the ones you like, or save the 3 best rated.`}
          </div>
        </div>
      </div>

      {/* Body: results on the left, the map and the business you're pointing at on the right */}
      {!showManual && (
      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 40, padding: isMobile ? '18px 14px 12px' : '28px 32px 20px' }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!smartDiscoveryLocked && (
            <BusinessSearchRow trade={trade} onAdd={addManual} isSubmitting={isCreatingVendor} />
          )}
          {!smartDiscoveryLocked && (
            // filters as a single row of toggles, off by default
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(
                [
                  ['4.5★ and up', 'minRating', 4.5],
                  ['Within 5 mi', 'maxDistanceMi', 5],
                  ['50+ reviews', 'minReviewCount', 50],
                  ['$–$$', 'maxPriceLevel', 2],
                ] as const
              ).map(([label, key, value]) => {
                const on = filters[key] === value;
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setFilters((f) => ({ ...f, [key]: on ? undefined : value }))}
                    style={{
                      height: 28,
                      padding: '0 11px',
                      border: 0,
                      borderRadius: 8,
                      background: on ? 'var(--tx-sel-bg)' : 'var(--tx-fill)',
                      boxShadow: on ? 'var(--tx-sel-shadow)' : 'none',
                      color: on ? C.text : C.muted,
                      fontFamily: fontStack,
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}>
                    {label}
                  </button>
                );
              })}
            </div>
          )}
          <div data-testid="finder-candidate-list" style={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'none' }}>
            {smartDiscoveryLocked ? (
              <div style={{ padding: '28px 8px', fontSize: 13, color: C.muted, lineHeight: 1.55 }}>
                Upload your inspection report and Hauser will match the right trades and recommend pros near the
                home. You can add a pro by hand meanwhile.
              </div>
            ) : isLoading ? (
              <div style={{ padding: '28px 8px', fontSize: 13, color: C.muted }}>Looking nearby…</div>
            ) : isError ? (
              <div style={{ padding: '28px 8px', fontSize: 13, color: C.muted }}>
                {vendorSearchError instanceof Error ? vendorSearchError.message : 'Search is unavailable right now.'}{' '}
                <button
                  type="button"
                  onClick={() => retryVendorSearch()}
                  style={{ border: 0, background: 'transparent', color: C.text, fontFamily: fontStack, fontSize: 13, cursor: 'pointer', padding: 0 }}>
                  Try again
                </button>
              </div>
            ) : candidates.length === 0 ? (
              <div style={{ padding: '28px 8px', fontSize: 13, color: C.muted }}>Nothing matches. Turn a filter off, or add a pro by hand.</div>
            ) : (
              candidates.map((c, i) => {
                const saved = isSavedForTrade(c);
                const picked = selectedIds.has(c.place_id);
                const meta = [
                  c.rating != null ? `★ ${c.rating.toFixed(1)}` : null,
                  c.reviewCount ? `${c.reviewCount} reviews` : null,
                  c.distance ? `${c.distance.value.toFixed(1)} ${c.distance.unit}` : null,
                  c.priceLevel != null ? '$'.repeat(Math.max(1, Math.min(4, c.priceLevel))) : null,
                ].filter(Boolean);
                return (
                  <div
                    key={c.place_id}
                    data-testid={`finder-candidate-${c.place_id}`}
                    className="hx-fd-row"
                    data-active={effectivePreviewId === c.place_id ? 'true' : undefined}
                    onMouseEnter={() => setPreviewPlaceId(c.place_id)}
                    onClick={() => setPreviewPlaceId(c.place_id)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '22px minmax(0, 1fr) 32px',
                      alignItems: 'center',
                      gap: 12,
                      minHeight: 62,
                      padding: '8px 10px',
                      borderRadius: 10,
                      borderTop: i ? '1px solid var(--tx-line)' : 'none',
                      cursor: 'default',
                    }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: picked ? 'var(--tx-lime)' : C.subtle, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                      {i + 1}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <span style={{ fontSize: 14, fontWeight: 500, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {c.name}
                        </span>
                        {GoogleG}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 500, color: C.muted, marginTop: 2 }}>{meta.join(' · ')}</div>
                    </div>
                    {saved ? (
                      <span style={{ fontSize: 11, color: C.subtle, textAlign: 'right' }}>Saved</span>
                    ) : (
                      <button
                        type="button"
                        aria-label={picked ? `Remove ${c.name} from shortlist` : `Add ${c.name} to shortlist`}
                        aria-pressed={picked}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(c.place_id);
                          setPreviewPlaceId(c.place_id);
                        }}
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          border: 0,
                          display: 'grid',
                          placeItems: 'center',
                          background: picked ? 'var(--tx-lime)' : 'var(--tx-fill-2)',
                          color: picked ? '#262626' : C.text,
                          fontSize: 15,
                          lineHeight: 1,
                          cursor: 'pointer',
                        }}>
                        {picked ? '✓' : '+'}
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {!isMobile && (
          <div style={{ flex: 1.1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ height: 280, borderRadius: 16, overflow: 'hidden', flexShrink: 0, background: C.bg3 }}>
              <ResultsMap
                candidates={candidates}
                activeId={effectivePreviewId}
                pickedIds={selectedIds}
                onPick={(id) => setPreviewPlaceId(id)}
              />
            </div>
            {previewCandidate && (
              <div data-testid="finder-profile" style={{ minHeight: 0, overflowY: 'auto', scrollbarWidth: 'none', padding: '0 4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18, fontWeight: 400, letterSpacing: '-0.02em', color: C.text }}>{previewCandidate.name}</span>
                  {GoogleG}
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                  {[
                    previewCandidate.rating != null ? `★ ${previewCandidate.rating.toFixed(1)}` : null,
                    previewCandidate.reviewCount ? `${previewCandidate.reviewCount} reviews` : null,
                    previewCandidate.category,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '72px minmax(0, 1fr)', rowGap: 8, columnGap: 12, marginTop: 14, fontSize: 13 }}>
                  {(
                    [
                      ['Phone', previewCandidate.phone],
                      ['Hours', previewCandidate.hoursLabel],
                      ['Address', previewCandidate.address],
                      ['Distance', previewCandidate.distance ? `${previewCandidate.distance.value.toFixed(1)} ${previewCandidate.distance.unit} from home` : null],
                    ] as const
                  )
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                      <Fragment key={k}>
                        <span style={{ color: C.subtle }}>{k}</span>
                        <span style={{ color: C.text }}>{v}</span>
                      </Fragment>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
                  {previewCandidate.website && (
                    <a href={previewCandidate.website} target="_blank" rel="noreferrer" className="hx-fd-link">
                      Website
                    </a>
                  )}
                  {previewCandidate.phone && (
                    <a href={`tel:${previewCandidate.phone}`} className="hx-fd-link">
                      Call
                    </a>
                  )}
                  {previewCandidate.googleMapsUri && (
                    <a href={previewCandidate.googleMapsUri} target="_blank" rel="noreferrer" className="hx-fd-link">
                      Open in Maps
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      )}

      {showManual && (
        <ManualForm
          trade={trade}
          onAdd={addManual}
          onCancel={() => setShowManual(false)}
          isSubmitting={isCreatingVendor}
          isMobile={isMobile}
        />
      )}

      {actionMessage && (
        <div role="status" style={{ margin: '0 24px 10px', fontSize: 12, color: C.subtle }}>
          {actionMessage}
        </div>
      )}

      {/* Footer: your shortlist, and saving it */}
      {!showManual && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: isMobile ? '14px 14px 16px' : '18px 32px 28px',
            borderTop: '1px solid var(--tx-line)',
            flexShrink: 0,
          }}>
          <button
            type="button"
            data-testid="finder-show-manual"
            onClick={() => setShowManual(true)}
            className="hx-fd-quiet"
            style={{ border: 0, background: 'transparent', padding: 0, fontFamily: fontStack, fontSize: 13, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Add someone you know
          </button>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', justifyContent: 'flex-end' }}>
            {candidates
              .filter((c) => selectedIds.has(c.place_id))
              .map((c) => (
                <span
                  key={c.place_id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    height: 28,
                    padding: '0 6px 0 10px',
                    borderRadius: 8,
                    background: 'var(--tx-fill)',
                    fontSize: 12,
                    color: C.text,
                    whiteSpace: 'nowrap',
                  }}>
                  {c.name}
                  <button
                    type="button"
                    aria-label={`Remove ${c.name}`}
                    onClick={() => toggle(c.place_id)}
                    style={{ border: 0, background: 'transparent', color: C.muted, cursor: 'pointer', padding: '0 4px', fontSize: 13 }}>
                    ×
                  </button>
                </span>
              ))}
          </div>
          {/* bottom right, one lime action: let Hauser pick the best three, or save the ones you picked */}
          {selectedCount === 0 && !smartDiscoveryLocked ? (
            <CriteriaTooltip>
              <button
                type="button"
                data-testid="finder-auto-populate"
                onClick={doAutoPopulate}
                disabled={isCreatingVendor || isLoading || candidates.length === 0}
                className="hx-fd-primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 9,
                  height: 40,
                  padding: '0 24px 0 8px',
                  border: 0,
                  borderRadius: 10,
                  background: 'var(--tx-lime)',
                  color: '#262626',
                  fontFamily: fontStack,
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  cursor: isCreatingVendor || isLoading ? 'not-allowed' : 'pointer',
                  opacity: isCreatingVendor || isLoading || candidates.length === 0 ? 0.5 : 1,
                  whiteSpace: 'nowrap',
                }}>
                <span
                  aria-hidden
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    display: 'grid',
                    placeItems: 'center',
                    background: 'rgba(38,38,38,0.12)',
                  }}>
                  {/* a small spark: picks by rating and review count */}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.4L12 3zM18.5 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2z"
                      fill="#262626"
                    />
                  </svg>
                </span>
                Save the 3 best rated
              </button>
            </CriteriaTooltip>
          ) : (
            <button
              type="button"
              data-testid="finder-save"
              onClick={saveSelected}
              disabled={selectedCount === 0 || isCreatingVendor}
              style={{
                height: 40,
                padding: '0 24px',
                border: 0,
                borderRadius: 10,
                background: selectedCount > 0 ? 'var(--tx-lime)' : 'var(--tx-fill)',
                color: selectedCount > 0 ? '#262626' : C.subtle,
                fontFamily: fontStack,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '-0.02em',
                cursor: selectedCount > 0 ? 'pointer' : 'default',
                whiteSpace: 'nowrap',
              }}>
              {selectedCount > 0
                ? `Save ${selectedCount} ${selectedCount === 1 ? 'pro' : 'pros'} to your directory`
                : 'Pick pros to save'}
            </button>
          )}
        </div>
      )}
    </MuiDialog>
  );
}

export default VendorFinderDialog;
